/**
 * Typed wrappers for every /api/v2/<resource> CRUD endpoint.
 *
 * list()   → GET  /api/v2/<resource>?limit=&offset=
 * get()    → GET  /api/v2/<resource>/:id
 * create() → POST /api/v2/<resource>
 * update() → PUT  /api/v2/<resource>/:id   (sends { _original_, _edited_ } body)
 * remove() → DELETE /api/v2/<resource>/:id
 */
import { api } from "./client";
import type {
  Template,
  ContentItem,
} from "../types";

const V2 = "/api/v2/sql";
// advertisers_content is owned by socialiq-content (Cloud SQL table + its GCS content bucket),
// not socialiq-sql — the content resource targets it via this base.
const CONTENT = "/api/v2/content";

// ── Generic CRUD factory ──────────────────────────────────────────────────────

interface ListParams {
  limit?: number;
  offset?: number;
  // Extra scalar query params a specific resource's list endpoint supports (e.g. advertisers_content's
  // `a_id` filter) — passed through verbatim so this generic factory doesn't need per-resource cases.
  [key: string]: string | number | boolean | undefined;
}

function resource<T, CreateBody = Partial<T>, UpdateBody = Partial<T>>(
  name: string,
  pk: keyof T,
  apiBase: string = V2,
) {
  const base = `${apiBase}/${name}`;
  return {
    list: (p: ListParams = {}) => {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(p)) {
        if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
      }
      const qs = q.toString();
      return api.get<T[]>(`${base}${qs ? `?${qs}` : ""}`);
    },
    get: (id: string | number) => api.get<T>(`${base}/${id}`),
    create: (body: CreateBody)  => api.post<T>(base, body),
    update: (id: string | number, edited: UpdateBody, original: UpdateBody) =>
      api.put<T>(`${base}/${id}`, { _original_: original, _edited_: edited }),
    patch: (id: string | number, partial: Partial<UpdateBody>) =>
      api.put<T>(`${base}/${id}`, partial),
    remove: (id: string | number) => api.delete<void>(`${base}/${id}`),
    _pk: pk,
  };
}

// ── Resources ─────────────────────────────────────────────────────────────────

export const templates   = resource<Template, Partial<Template>, Partial<Template>>(
  "autolauncher_template", "id",
);

// Create/UpdateBody are raw advertisers_content column rows (not ContentItems) — the CRUD
// create/update validate against real table columns with extra=forbid, so the hooks map
// ContentItem -> columns via contentItemToRow before writing. See useContent.ts.
export const content     = resource<ContentItem, Record<string, unknown>, Record<string, unknown>>(
  "advertisers_content", "id", CONTENT,
);

// SOC-792: the filters GET /api/v2/content/advertisers_content accepts on top of limit/offset. All
// optional and ANDed server-side; an unknown value is a 400. `status` is one review state or a
// comma list ("for_review,for_internal_review"); `scraped` is 1/0 (metadata.scraped present and
// truthy, or not). Pass to `content.list(...)` — ListParams' index signature admits it as-is.
// `deleted` (SOC-792 trash): 1 = ONLY the trash (soft-deleted rows, most recently trashed first);
// absent / 0 = live rows only, which is what every other read sees.
export type ContentListStatus = "approved" | "for_review" | "for_internal_review";
export interface ContentListParams extends ListParams {
  a_id?: number;
  status?: ContentListStatus | string;
  media_type?: "IMAGE" | "VIDEO";
  scraped?: 0 | 1;
  deleted?: 0 | 1;
}

export const creators    = resource<Record<string, unknown>>(
  "advertisers", "a_id",
);

export const rules       = resource<Record<string, unknown>>(
  "automation_rules", "ar_id",
);

export const subscriptions = resource<Record<string, unknown>>(
  "automation_rules_subscriptions", "arsu_id",
);

// NOTE: there is deliberately NO CRUD wrapper for automation_rules_executions or
// autolauncher_executions — socialiq-sql exposes neither as a CRUD resource. Executions are READ via
// named queries (getAutomationExecutions → /custom, getAutolauncherExecutions in actions.ts) and
// WRITTEN only by the launcher/automation engines (approve/deny go through /api/v2/automation/*), so a
// CRUD wrapper here would just be a 404 trap.

export const amazonTags  = resource<Record<string, unknown>>(
  "amazon_tags", "id",
);

// NOTE: the Impact "Program / Merchant" list is NOT a CRUD resource — it lives in impact_programs,
// served by the getImpactProgramsAll named query (see api/actions.ts:getImpactPrograms). The old
// `programs` resource here pointed at impact_accounts (credentials, no program_id/label) and rendered
// the Recipe dropdown as a long list of blank options; removed to avoid re-using the wrong table.

// ── Creators-admin port (CortexV2 /creators-admin, creator_management role) ───────────────────
export const impactAccounts = resource<Record<string, unknown>>(
  "impact_accounts", "ia_id",
);

export const fbAccounts = resource<Record<string, unknown>>(
  "fb_accounts", "fa_id",
);

// A creator's social platform handles — {a_id, handle, platform, is_hub}, one row per platform.
// The source of truth for "which social platforms is this creator on" (2026-09-29) — replaces
// advertisers.platforms (a frozen, no-longer-read/written JSON column; see resources/advertisers.py's
// module docstring on the backend). `is_hub` marks the one handle the redirector/Amazon-tag cache
// pick as the referer for a creator's links (services/redirector.py, core/cache_refresh.py).
export const advertisersSocials = resource<Record<string, unknown>>(
  "advertisers_socials", "as_id",
);

/**
 * The ad accounts the BRAND launcher reads. NOT the same table as `fbAccounts` above, despite the
 * same shape and the same fa_id space: `a_id` HERE is what getBrandLaunchConfig and brand_launch_requests
 * resolve through, so this is the link that decides whether a brand can be launched for.
 */
export const adnetFbAccounts = resource<Record<string, unknown>>(
  "facebook_accounts_adnet", "fa_id",
);

// ── Partner Network (CortexV2 /partner-network, partner_network role) — port of the Retool
// "Publishers"/"Redirects" apps ────────────────────────────────────────────────────────────────
export const publishers = resource<Record<string, unknown>>(
  "publishers", "p_id",
);

export const publishersRevShare = resource<Record<string, unknown>>(
  "publishers_rev_share", "prs_id",
);

export const publishersCpc = resource<Record<string, unknown>>(
  "publishers_cpc", "pcpc_id",
);

export const redirects = resource<Record<string, unknown>>(
  "redirects", "r_id",
);

export const campaigns = resource<Record<string, unknown>>(
  "campaigns", "c_id",
);

export const redirectDomains = resource<Record<string, unknown>>(
  "redirect_domains", "rd_id",
);

// Backed by a hand-written server router (socialiq_sql/core/amazon_internal_tags.py), not the
// generic build_router — create/update run real allocation-locking business logic. Shaped
// identically at the HTTP level, so this generic client works against it unmodified.
export const amazonInternalTags = resource<Record<string, unknown>>(
  "amazon_internal_tags", "ait_id",
);

export const advertiserRevShare = resource<Record<string, unknown>>(
  "advertiser_rev_share", "ars_id",
);

export const facebookPages = resource<Record<string, unknown>>(
  "facebook_pages", "fp_id",
);

export const bonusTargets = resource<Record<string, unknown>>(
  "bonus_targets", "bt_id",
);

export const bonusProgress = resource<Record<string, unknown>>(
  "bonus_progress", "bp_id",
);

// ── Creator CRM (SOC-604, CortexV2 /creator-crm, creator_crm role) ─────────────────────────────
// advertiser_onboarding is the pre-approval pipeline (applied/onboarding/rejected/approved) — see
// socialiq_sql/resources/advertiser_onboarding.py. advertisers_crm is an approved creator's CRM
// record — its own table+PK (advertiser_crm_id), a mandatory unique a_id FK — see
// socialiq_sql/resources/advertisers_crm.py. Not yet called from any UI control (no CRM-edit form
// exists); getCreatorCrmDetail/getCreatorCrmList (api/actions.ts) are the read path today.
export const advertiserOnboarding = resource<Record<string, unknown>>(
  "advertiser_onboarding", "onboarding_id",
);

export const advertisersCrm = resource<Record<string, unknown>>(
  "advertisers_crm", "advertiser_crm_id",
);

// creator_launch_activity — launch state per creator per boosting program (seeded from Monday.com's
// Launch Activity board), edited inline in Client Relations' Launch activity table. See
// socialiq_sql/resources/creator_launch_activity.py.
export const creatorLaunchActivity = resource<Record<string, unknown>>(
  "creator_launch_activity", "launch_activity_id",
);

// launch_strategy_guide — what each launch strategy means, one row per strategy, edited from the
// strategy guide in the Launch activity table. See socialiq_sql/resources/launch_strategy_guide.py.
export interface LaunchStrategyGuideRow {
  strategy_guide_id: number;
  strategy: string;
  description: string | null;
  created: string | null;
  updated: string | null;
}
export const launchStrategyGuide = resource<LaunchStrategyGuideRow>(
  "launch_strategy_guide", "strategy_guide_id",
);
